"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function BroadcastForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[var(--color-primary)] bg-[var(--color-secondary)] backdrop-blur-md text-center">
        <p className="font-default text-xs uppercase tracking-widest text-[var(--color-primary)] mb-4">
          REGISTRATION RECEIVED 
        </p>
        <p className="font-default text-xl text-[var(--color-text-dark)] mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default font-bold uppercase tracking-wider border-[var(--color-text-dark)] text-[var(--color-text-dark)] hover:bg-[var(--color-text-dark)] hover:text-[var(--color-secondary)] rounded-[var(--border-radius-lg)]">
          Register Another Entity
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[11px] uppercase tracking-widest text-[var(--color-primary)]">
                Company Representative *
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="bg-transparent border-0 border-b border-[var(--color-border)] rounded-[var(--border-radius-lg)] px-0 py-3 h-auto text-[var(--color-text-dark)] placeholder:text-[var(--color-text-dark)]/30 focus-visible:border-[var(--color-primary)] focus-visible:ring-0 focus-visible:shadow-md transition-all" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[11px] uppercase tracking-widest text-[var(--color-primary)]">
                Business Email *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="contact@company.com" {...field} className="bg-transparent border-0 border-b border-[var(--color-border)] rounded-[var(--border-radius-lg)] px-0 py-3 h-auto text-[var(--color-text-dark)] placeholder:text-[var(--color-text-dark)]/30 focus-visible:border-[var(--color-primary)] focus-visible:ring-0 focus-visible:shadow-md transition-all" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+]?[\d\s()-]{7,}$/,
        message: "Invalid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-[var(--color-primary)]">
              Business Phone
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-transparent border-0 border-b border-[var(--color-border)] rounded-[var(--border-radius-lg)] px-0 py-3 h-auto text-[var(--color-text-dark)] placeholder:text-[var(--color-text-dark)]/30 focus-visible:border-[var(--color-primary)] focus-visible:ring-0 focus-visible:shadow-md transition-all" />
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      <FormField control={control} name="company_name" rules={{
      required: "Company name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-[var(--color-primary)]">
              Company Name *
            </FormLabel>
            <FormControl>
              <Input placeholder="Global Trade Corp" {...field} className="bg-transparent border-0 border-b border-[var(--color-border)] rounded-[var(--border-radius-lg)] px-0 py-3 h-auto text-[var(--color-text-dark)] placeholder:text-[var(--color-text-dark)]/30 focus-visible:border-[var(--color-primary)] focus-visible:ring-0 focus-visible:shadow-md transition-all" />
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a category"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-[var(--color-primary)]">
              Industry Category *
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b border-[var(--color-border)] rounded-[var(--border-radius-lg)] px-0 py-3 h-auto text-[var(--color-text-dark)] data-[placeholder]:text-[var(--color-text-dark)]/30 focus-visible:border-[var(--color-primary)] focus-visible:ring-0 w-full">
                  <SelectValue placeholder="Select an industry" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--color-secondary)] border-[var(--color-border)] rounded-[var(--border-radius-lg)]">
                  <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="Logistics">Logistics</SelectItem>
                  <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                  <SelectItem value="Technology">Technology</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      <FormField control={control} name="start_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-[var(--color-primary)]">
              Expected Start Date
            </FormLabel>
            <FormControl>
              <Input type="date" {...field} className="bg-transparent border-0 border-b border-[var(--color-border)] rounded-[var(--border-radius-lg)] px-0 py-3 h-auto text-[var(--color-text-dark)] placeholder:text-[var(--color-text-dark)]/30 focus-visible:border-[var(--color-primary)] focus-visible:ring-0 focus-visible:shadow-md transition-all" />
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-600 bg-red-50">
          <p className="font-default text-xs uppercase tracking-widest text-red-600">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-auto bg-[var(--color-primary)] text-[var(--color-text-light)] font-default font-bold uppercase tracking-wider text-base py-5 rounded-[var(--border-radius-lg)] hover:bg-[var(--color-primary)]/90 transition-all duration-150">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Submitting...
          </> : <>
            Submit Registration
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function SupplierRegistration() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2593" id="supplierregistration" className="relative bg-[var(--color-secondary)] text-[var(--color-text-dark)] py-32 md:py-[200px] px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2 pointer-events-none">
        <div className="font-default text-[11px] uppercase tracking-[0.3em] text-[var(--color-text-dark)]/40 [writing-mode:vertical-rl] rotate-180">
          PARTNERSHIP 
        </div>
      </div>

      <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none">
        <div className="font-default text-[11px] uppercase tracking-[0.3em] text-[var(--color-text-dark)]/40 [writing-mode:vertical-rl]">
          PARTNERSHIP 
        </div>
      </div>

      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col items-center text-center mb-12 md:mb-16">
          <div className="h-px w-[120px] bg-[var(--color-accent)] mb-6" />
          <p className="font-default text-[11px] uppercase tracking-[0.3em] text-[var(--color-text-dark)]/60 mb-8">
            JOIN OUR NETWORK 
          </p>
          <h1 className="font-default font-bold uppercase tracking-tighter leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[8rem] text-[var(--color-primary)]">
            BECOME A{" "}
            <span className="text-[var(--color-accent)]">SUPPLIER.</span>
          </h1>
          <p className="font-default text-lg md:text-xl text-[var(--color-text-dark)]/80 max-w-[600px] mt-8 leading-relaxed">
            Partner with KTEX to streamline your global trade operations. Register your business today to access our exclusive procurement network.
          </p>
        </div>

        <div className="max-w-[720px] mx-auto">
          <div className="relative bg-[var(--color-secondary)] border border-[var(--color-border)] p-8 sm:p-12 md:p-16 rounded-[var(--border-radius-lg)] shadow-md">
            <FormProvider formKey="supplier_form" formId="supplier_form" sectionName="supplierregistration" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company_name: "",
            service_interest: "",
            start_date: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            company_name: {
              required: true
            },
            service_interest: {
              required: true
            }
          }} submitText="Submit Registration" successMessage="Registration submitted successfully. Our team will review your credentials and contact you shortly." errorMessage="Submission failed. Please verify your details and try again.">
              <BroadcastForm />
            </FormProvider>
          </div>

          <div className="flex items-center justify-center gap-3 mt-8">
            <div className="h-px w-12 bg-[var(--color-border)]" />
            <p className="font-default text-[10px] uppercase tracking-[0.3em] text-[var(--color-text-dark)]/50">
              KTEX TRADE 
            </p>
            <div className="h-px w-12 bg-[var(--color-border)]" />
          </div>
        </div>
      </div>
    </section>;
}